/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { Fragment } from 'preact';
import { RatingStar } from './RatingStar';
import { useUser } from '#hooks/UNSAFE_useUser';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { RatingGaugeStyles } from './themes/RatingGaugeStyles.css';
import { RatingGaugeRedwoodTheme } from './themes/redwood/RatingGaugeTheme';
import { RatingStarColorType } from './ratingUtils';

type Props = {
  fillRatio: number;
  isDisabled?: boolean;
  isReadonly?: boolean;
  color: RatingStarColorType;
};

const RatingGaugeItem = ({ fillRatio, isDisabled, isReadonly, color }: Props) => {
  const { direction } = useUser();
  const isRtl = direction === 'rtl';
  const {
    styles: { fractionalStarBaseStyle }
  } = useComponentTheme<never, RatingGaugeStyles>(RatingGaugeRedwoodTheme);

  if (fillRatio === 1 || fillRatio === 0) {
    return (
      <RatingStar
        isSelected={fillRatio === 1}
        isDisabled={isDisabled}
        isReadOnly={isReadonly}
        color={color}
      />
    );
  }

  // if fillRatio is not 1 or zero, absolutely position selected star above the
  // unselected star and clip both to form partially selected star.
  return (
    <Fragment>
      <div
        class={fractionalStarBaseStyle}
        style={{
          clipPath: `inset(0% ${isRtl ? (1 - fillRatio) * 100 : 0}% 0% ${
            isRtl ? 0 : fillRatio * 100
          }%)`
        }}>
        <RatingStar
          isSelected={false}
          isDisabled={isDisabled}
          isReadOnly={isReadonly}
          color={color}
        />
      </div>
      <div
        class={fractionalStarBaseStyle}
        style={{
          clipPath: `inset(0% ${isRtl ? 0 : (1 - fillRatio) * 100}% 0% ${
            isRtl ? (1 - fillRatio) * 100 : 0
          }%)`
        }}>
        <RatingStar
          isSelected={true}
          isDisabled={isDisabled}
          isReadOnly={isReadonly}
          color={color}
        />
      </div>
    </Fragment>
  );
};

export { RatingGaugeItem };
